import {
  BubbleChatDoneIcon,
  Building03Icon,
  ChartLineData01Icon,
  CircleCheckIcon,
  ClipboardIcon,
  ClipboardCheckIcon,
  ClipboardPasteIcon,
  Clock01Icon,
  DashboardSquare01Icon,
  DatabaseIcon,
  Download01Icon,
  EyeIcon,
  FileEditIcon,
  GridViewIcon,
  HelpCircleIcon,
  Invoice03Icon,
  MoneyReceiveSquareIcon,
  Link01Icon,
  Mail01Icon,
  Message01Icon,
  Mic01Icon,
  Notification03Icon,
  PuzzleIcon,
  RefreshIcon,
  Robot01Icon,
  Rocket01Icon,
  Settings01Icon,
  SearchList01Icon,
  SlidersHorizontalIcon,
  UserCheck01Icon,
  UserGroupIcon,
  UserIcon,
  UserEdit01Icon,
  Wallet01Icon,
} from '@hugeicons/core-free-icons'

/**
 * Canonical registry for internal app navigation, access, search, and grouping.
 * Category controls Launch Center grouping, order, and color. Teams, tags,
 * aliases, and descriptions include function, action, and system terms for
 * natural-language fuzzy search.
 * Add each discoverable app here instead of creating another navigation list.
 * If an app's primary category or team ownership is unclear, confirm it with the
 * user before adding or changing the entry.
 */
export const internalToolCategories = [
  'Revenue & Analytics',
  'Renewals & Leasing',
  'Residents & Communications',
  'Portfolios & Accounts',
  'Integrations & Data',
  'AI, Quality & Platform',
]

export const internalTools = [
  {
    key: 'appwork-residents',
    title: 'AppWork Residents',
    href: '/appwork-residents',
    routePrefixes: ['/appwork-residents'],
    icon: UserEdit01Icon,
    motion: 'residentEdit',
    category: 'Integrations & Data',
    teams: ['Integrations'],
    tags: ['AppWork', 'residents', 'tenancy', 'occupancy', 'status', 'move out', 'notice'],
    aliases: ['AppWork tenancy updates', 'resident status changes'],
    description: 'Review and update AppWork occupancy, move-in, move-out, and notice dates.',
    readOnlyAllowed: true,
  },
  {
    key: 'llm-evaluation',
    title: 'Renewal LLM Evaluation',
    href: '/llm-evaluation',
    routePrefixes: ['/llm-evaluation'],
    icon: Robot01Icon,
    motion: 'robot',
    category: 'AI, Quality & Platform',
    teams: ['Renewals & Leasing', 'AI & Platform'],
    tags: ['renewals', 'AI', 'evaluation', 'prompts', 'review'],
    aliases: ['prompt evaluation', 'renewal classifier'],
    description: 'Evaluate and improve renewal conversation prompts.',
  },
  {
    key: 'renewal-health',
    title: 'Renewal Health Check',
    href: '/renewal-health',
    routePrefixes: ['/renewal-health'],
    icon: CircleCheckIcon,
    motion: 'circleCheck',
    category: 'Renewals & Leasing',
    teams: ['Renewals & Leasing', 'Executive'],
    tags: ['renewals', 'risk', 'retention', 'monitor'],
    aliases: ['renewal risk', 'retention health'],
    description: 'Monitor renewal risk and open account-level detail.',
  },
  {
    key: 'charge-code-explorer',
    title: 'Charge Code Explorer',
    href: '/charge-code-explorer',
    routePrefixes: ['/charge-code-explorer'],
    icon: SearchList01Icon,
    motion: 'chargeCodeSearch',
    category: 'Integrations & Data',
    teams: ['Onboarding & Accounts', 'Integrations'],
    tags: ['charge codes', 'delinquency', 'subsidy', 'PMS', 'read only', 'export'],
    aliases: ['charge code viewer', 'reminder exclusions', 'code discovery'],
    description: 'View and export stored charge codes for client review.',
  },
  {
    key: 'charge-code-mappings',
    title: 'Charge Code Mappings',
    href: '/charge-code-mappings',
    routePrefixes: ['/charge-code-mappings'],
    icon: SlidersHorizontalIcon,
    motion: 'sliders',
    category: 'Integrations & Data',
    teams: ['Onboarding & Accounts', 'Billing'],
    tags: ['charge codes', 'mapping', 'lease audit', 'configure'],
    aliases: ['charge mapping', 'ledger codes'],
    description: 'Map property charge codes for lease-audit workflows.',
  },
  {
    key: 'active-products',
    title: 'Active Products',
    href: '/active-products',
    routePrefixes: ['/active-products'],
    icon: GridViewIcon,
    motion: 'grid',
    category: 'Portfolios & Accounts',
    teams: ['Onboarding & Accounts', 'Executive'],
    tags: ['products', 'portfolio', 'configuration'],
    aliases: ['enabled products', 'product access'],
    description: 'Review active ResiDesk products across accounts.',
  },
  {
    key: 'account-slack-notifications',
    title: 'Account Slack Notifications',
    href: '/account-slack-notifications',
    routePrefixes: ['/account-slack-notifications'],
    icon: Message01Icon,
    motion: 'message',
    category: 'Residents & Communications',
    teams: ['Onboarding & Accounts', 'Resident Experience'],
    tags: ['Slack', 'notifications', 'accounts', 'configure'],
    aliases: ['Slack alerts', 'account alerts'],
    description: 'Configure account-level Slack notifications.',
    hideForReadOnly: true,
  },
  {
    key: 'assistant-bridge-packages',
    title: 'ResiDesk Internal Agent',
    href: '/assistant-bridge-packages',
    routePrefixes: ['/assistant-bridge-packages'],
    icon: Download01Icon,
    motion: 'download',
    category: 'AI, Quality & Platform',
    teams: ['AI & Platform'],
    tags: ['agent', 'MCP', 'install', 'desktop'],
    aliases: ['AssistantBridge packages', 'internal assistant'],
    description: 'Install and manage the ResiDesk internal agent.',
    corporateAllowed: true,
    readOnlyAllowed: true,
  },
  {
    key: 'assistant-bridge-usage',
    title: 'MCP usage',
    href: '/assistant-bridge-usage',
    routePrefixes: ['/assistant-bridge-usage'],
    icon: Link01Icon,
    motion: 'link',
    category: 'AI, Quality & Platform',
    teams: ['AI & Platform', 'Executive'],
    tags: ['MCP', 'agent', 'usage', 'analytics'],
    aliases: ['AssistantBridge usage', 'tool usage'],
    description: 'Inspect internal-agent and MCP tool usage.',
  },
  {
    key: 'account-management',
    title: 'Account Management',
    href: '/account-management',
    routePrefixes: ['/account-management'],
    icon: UserGroupIcon,
    motion: 'users',
    category: 'Portfolios & Accounts',
    teams: ['Onboarding & Accounts'],
    tags: ['accounts', 'portfolios', 'users', 'configure'],
    aliases: ['customer accounts', 'account settings'],
    description: 'Manage customer accounts and portfolio relationships.',
    corporateAllowed: true,
  },
  {
    key: 'yardi-etl-generator',
    title: 'Yardi ETL Generator',
    href: '/yardi-etl-generator',
    routePrefixes: ['/yardi-etl-generator'],
    icon: DatabaseIcon,
    motion: 'database',
    category: 'Integrations & Data',
    teams: ['Integrations', 'Onboarding & Accounts'],
    tags: ['Yardi', 'ETL', 'data', 'integration', 'generate'],
    aliases: ['Yardi pipeline', 'Yardi import'],
    description: 'Generate Yardi ETL configuration for a portfolio.',
    hideForReadOnly: true,
  },
  {
    key: 'entrata-integration-dashboard',
    title: 'Entrata Integration',
    href: '/entrata-integration-dashboard',
    routePrefixes: ['/entrata-integration-dashboard'],
    icon: PuzzleIcon,
    motion: 'puzzle',
    category: 'Integrations & Data',
    teams: ['Integrations', 'Onboarding & Accounts'],
    tags: ['Entrata', 'PMS', 'integration', 'monitor'],
    aliases: ['Entrata dashboard', 'Entrata sync'],
    description: 'Inspect and manage Entrata integration state.',
    readOnlyAllowed: true,
  },
  {
    key: 'property-data-health',
    title: 'Property Data Health',
    href: '/property-data-health',
    routePrefixes: ['/property-data-health'],
    icon: Building03Icon,
    motion: 'building',
    category: 'Integrations & Data',
    teams: ['Integrations'],
    tags: ['PMS', 'properties', 'data', 'status', 'receipts'],
    aliases: ['building health', 'Entrata', 'Yardi', 'Rent Manager'],
    description: 'Review property records, source-reported status, and PMS receipt evidence.',
    corporateAllowed: true,
  },
  {
    key: 'business-review-roi',
    title: 'Business Review ROI',
    href: '/business-review-roi',
    routePrefixes: ['/business-review-roi'],
    icon: DashboardSquare01Icon,
    motion: 'dashboard',
    category: 'Revenue & Analytics',
    teams: ['Executive'],
    tags: ['ROI', 'business review', 'analytics', 'value'],
    aliases: ['QBR ROI', 'customer value'],
    description: 'Prepare business-review return-on-investment analysis.',
    adminOnly: true,
    hideForReadOnly: true,
  },
  {
    key: 'customer-mrr',
    title: 'MRR by Customer',
    href: '/customer-mrr',
    routePrefixes: ['/customer-mrr', '/snowflake-mrr'],
    icon: Wallet01Icon,
    motion: 'wallet',
    category: 'Revenue & Analytics',
    teams: ['Executive', 'Billing'],
    tags: ['MRR', 'revenue', 'customers', 'Snowflake', 'analytics'],
    aliases: ['monthly recurring revenue', 'customer revenue'],
    description: 'Compare monthly recurring revenue by customer.',
  },
  {
    key: 'billing-cash',
    title: 'Billing & Cash',
    href: '/billing-cash',
    routePrefixes: ['/billing-cash'],
    icon: Invoice03Icon,
    motion: 'invoice',
    category: 'Revenue & Analytics',
    teams: ['Billing', 'Executive'],
    tags: ['billing', 'cash', 'collections', 'invoices', 'forecast'],
    aliases: ['accounts receivable', 'A/R', 'payments'],
    description: 'Review billing, collections, and cash forecasts.',
  },
  {
    key: 'record-payments',
    title: 'Record invoice payments',
    href: '/record-payments',
    routePrefixes: ['/record-payments'],
    icon: MoneyReceiveSquareIcon,
    motion: 'receivePayment',
    category: 'Revenue & Analytics',
    teams: ['Billing'],
    tags: ['Stripe', 'partial payments', 'checks', 'ACH', 'wire', 'invoices'],
    aliases: ['out of band payments', 'record partial payment', 'Allan'],
    description:
      'Record invoice payments received outside Stripe and track the remaining invoice balance.',
  },
  {
    key: 'route-activity',
    title: 'Dashboard usage and exports',
    href: '/route-activity',
    routePrefixes: ['/route-activity'],
    icon: EyeIcon,
    motion: 'eye',
    category: 'Revenue & Analytics',
    teams: ['Executive'],
    tags: ['dashboard', 'usage', 'exports', 'analytics', 'activity', 'Chrome extension', 'Tasks'],
    aliases: ['route activity', 'dashboard adoption', 'extension usage'],
    description: 'Compare Chrome extension and web usage, task replies, and report exports.',
  },
  {
    key: 'conversation-qa',
    title: 'Conversation QA',
    href: '/conversation-qa',
    routePrefixes: ['/conversation-qa'],
    icon: ClipboardIcon,
    motion: 'clipboard',
    category: 'AI, Quality & Platform',
    teams: ['AI & Platform', 'Resident Experience'],
    tags: ['conversations', 'QA', 'AI', 'review', 'quality'],
    aliases: ['conversation review', 'message QA'],
    description: 'Review conversation quality and AI behavior.',
  },
  {
    key: 'default-associate-name',
    title: 'Default Associate Name',
    href: '/default-associate-name',
    routePrefixes: ['/default-associate-name'],
    icon: UserIcon,
    motion: 'user',
    category: 'Portfolios & Accounts',
    teams: ['Onboarding & Accounts'],
    tags: ['associates', 'accounts', 'defaults', 'configure'],
    aliases: ['default agent name', 'associate identity'],
    description: 'Configure the default associate name for an account.',
  },
  {
    key: 'front-email-lists',
    title: 'Front Email Lists',
    href: '/front-email-lists',
    routePrefixes: ['/front-email-lists'],
    icon: Mail01Icon,
    motion: 'mail',
    category: 'Residents & Communications',
    teams: ['Onboarding & Accounts', 'Resident Experience'],
    tags: ['Front', 'email', 'lists', 'communications'],
    aliases: ['distribution lists', 'Front lists'],
    description: 'Manage Front email distribution lists.',
  },
  {
    key: 'portfolio-assignments',
    title: 'Portfolio Assignments',
    href: '/portfolio-assignments',
    routePrefixes: ['/portfolio-assignments'],
    icon: UserCheck01Icon,
    motion: 'userCheck',
    category: 'Portfolios & Accounts',
    teams: ['Onboarding & Accounts'],
    tags: ['portfolios', 'assignments', 'accounts', 'ownership'],
    aliases: ['portfolio owners', 'assign accounts'],
    description: 'Assign internal owners to customer portfolios.',
  },
  {
    key: 'office-hours',
    title: 'After-hours & Office Hours',
    href: '/office-hours',
    routePrefixes: ['/office-hours'],
    icon: Clock01Icon,
    motion: 'clock',
    category: 'Residents & Communications',
    teams: ['Onboarding & Accounts', 'Resident Experience'],
    tags: ['office hours', 'after-hours', 'scheduling', 'communications'],
    aliases: ['business hours', 'after hours'],
    description: 'Configure office-hours and after-hours behavior.',
    hideForReadOnly: true,
  },
  {
    key: 'portfolio-onboarding',
    title: 'Portfolio Onboarding',
    href: '/portfolio-onboarding',
    routePrefixes: ['/portfolio-onboarding'],
    icon: ClipboardPasteIcon,
    motion: 'clipboardPaste',
    category: 'Portfolios & Accounts',
    teams: ['Onboarding & Accounts'],
    tags: ['portfolio', 'onboarding', 'setup', 'launch'],
    aliases: ['new portfolio', 'implementation setup'],
    description: 'Track and complete portfolio onboarding.',
    hideForReadOnly: true,
  },
  {
    key: 'leasing-management',
    title: 'Leasing AI Management',
    href: '/leasing-management',
    routePrefixes: ['/leasing-management'],
    icon: Rocket01Icon,
    motion: 'rocket',
    category: 'Renewals & Leasing',
    teams: ['Renewals & Leasing', 'AI & Platform'],
    tags: ['leasing', 'AI', 'management', 'configure'],
    aliases: ['leasing assistant', 'leasing automation'],
    description: 'Configure and monitor Leasing AI behavior.',
    hideForReadOnly: true,
  },
  {
    key: 'survey-control',
    title: 'Survey Control',
    href: '/survey-control',
    routePrefixes: ['/survey-control'],
    icon: Settings01Icon,
    motion: 'settings',
    category: 'Residents & Communications',
    teams: ['Onboarding & Accounts', 'Resident Experience'],
    tags: ['surveys', 'residents', 'settings', 'configure'],
    aliases: ['resident surveys', 'survey settings'],
    description: 'Manage resident survey configuration and records.',
  },
  {
    key: 'property-faq-collector',
    title: 'Property FAQ Collector',
    href: '/property-faq-collector',
    routePrefixes: ['/property-faq-collector'],
    icon: HelpCircleIcon,
    motion: 'help',
    category: 'Residents & Communications',
    teams: ['Onboarding & Accounts', 'AI & Platform'],
    tags: ['property', 'FAQ', 'knowledge', 'content'],
    aliases: ['property questions', 'FAQ collection'],
    description: 'Collect and review property FAQ knowledge.',
  },
  {
    key: 'knowledge-gaps',
    title: 'Knowledge Gaps',
    href: '/knowledge-gaps',
    routePrefixes: ['/knowledge-gaps'],
    icon: HelpCircleIcon,
    motion: 'help',
    category: 'AI, Quality & Platform',
    teams: ['Onboarding & Accounts', 'AI & Platform'],
    tags: ['knowledge', 'faq', 'documents', 'unanswered', 'coverage'],
    aliases: ['unanswered questions', 'coverage gaps', 'missing faqs'],
    description: 'Questions the knowledge base could not answer, per property.',
    readOnlyAllowed: true,
  },
  {
    key: 'learned-faqs',
    title: 'Learned FAQs',
    href: '/learned-faqs',
    routePrefixes: ['/learned-faqs'],
    icon: HelpCircleIcon,
    motion: 'help',
    category: 'AI, Quality & Platform',
    teams: ['Onboarding & Accounts', 'AI & Platform'],
    tags: ['knowledge', 'faq', 'review', 'learned', 'documents'],
    aliases: ['faq review queue', 'learned answers'],
    description: 'Review answers staff gave residents before they join the knowledge base.',
  },
  {
    key: 'answer-cards',
    title: 'Answer cards',
    href: '/answer-cards',
    routePrefixes: ['/answer-cards'],
    icon: BubbleChatDoneIcon,
    motion: 'message',
    category: 'AI, Quality & Platform',
    teams: ['Onboarding & Accounts', 'AI & Platform'],
    tags: ['knowledge', 'answers', 'cache', 'faq', 'documents'],
    aliases: ['stored answers', 'precomputed answers', 'answer cache'],
    description:
      "See and switch off the stored answers the AI serves for a property's most-asked questions.",
  },
  {
    key: 'public-markdown-documents',
    title: 'Public Markdown',
    href: '/public-markdown-documents',
    routePrefixes: ['/public-markdown-documents'],
    icon: FileEditIcon,
    motion: 'file',
    category: 'Residents & Communications',
    teams: ['Onboarding & Accounts', 'Resident Experience'],
    tags: ['markdown', 'documents', 'public', 'content'],
    aliases: ['public docs', 'markdown pages'],
    description: 'Create and manage public Markdown documents.',
    hideForReadOnly: true,
  },
  {
    key: 'announcement-rules',
    title: 'Announcement Rules',
    href: '/announcement-rules',
    routePrefixes: ['/announcement-rules'],
    icon: Notification03Icon,
    motion: 'notification',
    category: 'Residents & Communications',
    teams: ['Resident Experience', 'Onboarding & Accounts'],
    tags: ['announcements', 'rules', 'residents', 'configure'],
    aliases: ['announcement automation', 'resident announcements'],
    description: 'Configure when resident announcements are sent.',
  },
  {
    key: 'lease-audit-review',
    title: 'Lease Audit Review',
    href: '/lease-audit-review',
    routePrefixes: ['/lease-audit-review'],
    icon: ClipboardCheckIcon,
    motion: 'clipboardCheck',
    category: 'Renewals & Leasing',
    teams: ['Billing', 'Renewals & Leasing'],
    tags: ['leases', 'audit', 'review', 'decisions'],
    aliases: ['lease review', 'lease findings'],
    description: 'Review lease-audit findings and decisions.',
  },
  {
    key: 'lease-audit-trials',
    title: 'Lease Audit Trials',
    href: '/lease-audit-trials',
    routePrefixes: ['/lease-audit-trials', '/lease-audit-charters'],
    icon: Rocket01Icon,
    motion: 'rocket',
    category: 'AI, Quality & Platform',
    teams: ['Billing', 'AI & Platform'],
    tags: ['leases', 'audit', 'trials', 'testing', 'quality'],
    aliases: ['audit charters', 'lease testing'],
    description: 'Configure and run lease-audit trials.',
    readOnlyAllowed: true,
  },
  {
    key: 'pg-boss-dashboard',
    title: 'Queue Console',
    href: '/pg-boss-dashboard',
    routePrefixes: ['/pg-boss-dashboard'],
    icon: RefreshIcon,
    motion: 'refresh',
    category: 'AI, Quality & Platform',
    teams: ['AI & Platform'],
    tags: ['queue', 'jobs', 'runtime', 'monitor', 'pg-boss'],
    aliases: ['job queue', 'background jobs'],
    description: 'Inspect internal background-job queue state.',
    adminOnly: true,
    corporateAllowed: true,
    readOnlyAllowed: true,
  },
  {
    key: 'voice-mode',
    title: 'Voice Mode Dogfood',
    href: '/voice-mode',
    routePrefixes: ['/voice-mode'],
    icon: Mic01Icon,
    motion: 'mic',
    category: 'AI, Quality & Platform',
    teams: ['AI & Platform'],
    tags: ['voice', 'dogfood', 'AI', 'testing'],
    aliases: ['voice mode', 'voice assistant'],
    description: 'Test ResiDesk voice-mode experiences.',
    adminOnly: true,
    corporateAllowed: true,
  },
  {
    key: 'voice-ops',
    title: 'Voice Ops',
    href: '/voice-ops',
    routePrefixes: ['/voice-ops'],
    icon: ChartLineData01Icon,
    motion: 'chartLine',
    category: 'AI, Quality & Platform',
    teams: ['AI & Platform'],
    tags: ['voice', 'cost', 'performance', 'calls', 'AI'],
    aliases: ['voice performance', 'voice cost', 'call facts'],
    description: 'Voice assistant performance, cost, and failures per client and property.',
    corporateAllowed: true,
    readOnlyAllowed: true,
  },
]
